/**
 * @module immersive/StageContext
 * @description Send Love Live state that sits beside GivingContext:
 *   - celebrate(): queue a Cupid celebration for a Shot (donor's own screen only)
 *   - SIMULATED activity: love-stream events and per-cause "recent gifts" counts
 *   - rebel preview: demo toggle to see the earned Rebel Arrow unlocked
 *
 * // TEMPORARY: simulated love-stream and activity.
 *   What caused this: the real stream is Durable Objects fed ONLY by Neon-
 *   confirmed gifts (vision §10), which needs the backend and Neon access.
 *   Why we chose it: the room must see what the stream FEELS like; every
 *   simulated surface is labeled "Simulated" in the UI, and the donor's own
 *   Shots are never added to it (vision §10.2 truthfulness rule).
 *   Side-effects: none; timers only, nothing stored or sent.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type JSX, type ReactNode } from "react";
import { useGiving } from "../app/GivingContext";
import { TOUR_SHOT_EVENT } from "../components/TierTour";
import { LEGEND_ARROWS, type LegendArrow } from "../domain/arrows";
import type { Cause } from "../domain/types";

/** One love-stream line. */
export interface StreamEvent {
    id: number;
    causeId: string;
    arrow: LegendArrow;
    displayName: string;
    comment: string | null;
    at: number;
}

/** A pending celebration on the donor's own screen. */
export interface Celebration {
    id: number;
    arrows: LegendArrow[];
    causeTitle: string;
}

interface StageState {
    stream: StreamEvent[];
    recentCount: (causeId: string) => number;
    celebration: Celebration | null;
    celebrate: (arrows: LegendArrow[], cause: Cause) => void;
    endCelebration: () => void;
    rebelPreview: boolean;
    setRebelPreview: (on: boolean) => void;
}

const StageContext = createContext<StageState | null>(null);

/** Opt-in display names: "A friend" is the default (vision §10.5). */
const NAMES: readonly string[] = ["A friend", "A friend", "A friend", "A friend in Brooklyn", "A friend in Toronto", "A friend in Kingston", "A friend in Miami", "A friend in London"];
/** Short, moderated-style comments (≤ 80 chars, vision §10.5). */
const COMMENTS: readonly (string | null)[] = [null, null, "One love!", "From Mo Bay with love", "For the children", "Proud to help", null, "Big up yourselves", "Jamaica strong"];
/** Small arrows dominate a real stream; this keeps the demo believable. */
const STREAM_ARROWS: readonly LegendArrow[] = LEGEND_ARROWS.filter((a: LegendArrow) => a.availability === "standard" && a.faceUsd <= 100);
/** How long a gift counts as "recent" for lobby bubbles. */
const RECENT_MS: number = 60_000;

/** @returns a random element. */
function pick<T>(list: readonly T[]): T {
    return list[Math.floor(Math.random() * list.length)] as T;
}

/** Provides Send Love Live state. */
export function StageProvider({ children }: { children: ReactNode }): JSX.Element {
    const { causes, causeById, dispatchCart } = useGiving();
    const [stream, setStream] = useState<StreamEvent[]>([]);
    const [celebration, setCelebration] = useState<Celebration | null>(null);
    const [rebelPreview, setRebelPreview] = useState<boolean>(false);
    const [now, setNow] = useState<number>(Date.now());

    // Simulated stream: one gift every 2.5–5.5 s on a random cause, weighted to featured ones.
    useEffect(() => {
        if (causes === null || causes.length === 0) return;
        let timer: number;
        let id: number = 0;
        const weighted: Cause[] = causes.flatMap((c: Cause) => (c.featured ? [c, c, c] : [c]));
        const tick = (): void => {
            const event: StreamEvent = {
                id: ++id, causeId: pick(weighted).id, arrow: pick(STREAM_ARROWS),
                displayName: pick(NAMES), comment: pick(COMMENTS), at: Date.now(),
            };
            setStream((prev: StreamEvent[]) => [event, ...prev].slice(0, 60));
            setNow(Date.now());
            timer = window.setTimeout(tick, 2500 + Math.random() * 3000);
        };
        timer = window.setTimeout(tick, 900);
        return () => window.clearTimeout(timer);
    }, [causes]);

    const recentCount = useCallback(
        (causeId: string): number => stream.filter((e: StreamEvent) => e.causeId === causeId && now - e.at < RECENT_MS).length,
        [stream, now],
    );

    const celebrate = useCallback((arrows: LegendArrow[], cause: Cause): void => {
        setCelebration({ id: Date.now(), arrows, causeTitle: cause.title });
    }, []);

    // Demo tour "Play one": a real US$20 Garvey Shot on the given stage.
    useEffect(() => {
        const onShot = (e: Event): void => {
            const cause: Cause | undefined = causeById((e as CustomEvent<string>).detail);
            const garvey: LegendArrow | undefined = LEGEND_ARROWS.find((a: LegendArrow) => a.id === "garvey-20");
            if (cause === undefined || garvey === undefined) return;
            dispatchCart({ type: "increment", causeId: cause.id, amountUsd: garvey.faceUsd });
            celebrate([garvey], cause);
        };
        window.addEventListener(TOUR_SHOT_EVENT, onShot);
        return () => window.removeEventListener(TOUR_SHOT_EVENT, onShot);
    }, [causeById, dispatchCart, celebrate]);

    const value: StageState = useMemo(() => ({
        stream, recentCount, celebration, celebrate, endCelebration: () => setCelebration(null), rebelPreview, setRebelPreview,
    }), [stream, recentCount, celebration, celebrate, rebelPreview]);

    return <StageContext.Provider value={value}>{children}</StageContext.Provider>;
}

/** @returns Send Love Live state. @throws outside StageProvider. */
export function useStage(): StageState {
    const ctx: StageState | null = useContext(StageContext);
    if (ctx === null) throw new Error("[StageContext] useStage() called outside <StageProvider>.");
    return ctx;
}
